import { useState } from "react";
import {
  NEEDS_NO_VALUE, OPERATORS_FOR, OPERATOR_LABEL,
  describe, fieldsFor, specFor,
  type Operator, type Rule,
} from "../lib/advancedFilters";

/* The second layer of filtering, folded away until somebody wants it.
 *
 * James, 2026-09-15: core fields first, advanced fields hidden until needed.
 * So the screen's own controls stay in the bar above and this is one button
 * next to them.
 *
 * Two things it will not do:
 *
 * It never hides a filter that is on. A closed panel still shows a chip per
 * rule, because a narrowed list that looks unnarrowed is the screen lying
 * about its own contents, and that is the one failure this application cannot
 * afford.
 *
 * It never applies half a rule. A row with no value yet is kept on screen and
 * left out of the query, so choosing a field does not empty the table before
 * the person has said what they are looking for.
 */
export function AdvancedFilters({
  view, rules, onChange, embedded = false,
}: {
  /** The api view these fields belong to, for example "company". */
  view: string;
  rules: readonly Rule[];
  onChange: (next: Rule[]) => void;
  /** Inside the list chrome's own filter panel, which has the button and
   *  shows the chips, so this renders the rows alone, always open. */
  embedded?: boolean;
}) {
  const fields = fieldsFor(view);
  // Open on its own if something is already set, which is what happens when
  // somebody follows a link to a narrowed list.
  const [openState, setOpen] = useState(rules.length > 0);
  const open = embedded || openState;
  if (!fields.length) return null;

  const set = (i: number, patch: Partial<Rule>) =>
    onChange(rules.map((r, n) => (n === i ? { ...r, ...patch } : r)));

  const add = () => {
    const first = fields[0];
    if (!first) return;
    const op = OPERATORS_FOR[first.type][0];
    if (!op) return;
    onChange([...rules, { field: first.key, op, value: "" }]);
    setOpen(true);
  };

  const remove = (i: number) => onChange(rules.filter((_, n) => n !== i));

  /* Changing the field has to change the operator too. "contains" carried onto
     a unit count is a filter the database refuses, and the person who changed
     one dropdown did not ask for that. */
  const changeField = (i: number, key: string) => {
    const spec = specFor(view, key);
    if (!spec) return;
    const allowed = OPERATORS_FOR[spec.type];
    const current = rules[i]?.op;
    const fallback = allowed[0];
    if (!fallback) return;
    set(i, {
      field: key,
      op: current && allowed.includes(current) ? current : fallback,
      value: spec.type === "boolean" ? "true" : "",
    });
  };

  return (
    <div className="advanced">
      {embedded ? null : (
        <div className="bar">
          <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
            {rules.length ? `More filters (${rules.length})` : "More filters"}
          </button>

          {/* Visible whether the panel is open or shut. */}
          {!open && rules.map((r, i) => (
            <span className="chip" key={`${r.field}-${i}`}>
              {describe(view, r)}
              <button type="button" aria-label={`Remove the filter ${describe(view, r)}`}
                      onClick={() => remove(i)}>x</button>
            </span>
          ))}
        </div>
      )}

      {open && (
        <div className={embedded ? "advanced-rows embedded" : "advanced-rows"}>
          {rules.map((r, i) => {
            const spec = specFor(view, r.field);
            const type = spec?.type ?? "text";
            const needsValue = !NEEDS_NO_VALUE.includes(r.op);
            return (
              <div className="advanced-row" key={i}>
                <label className="sr-only" htmlFor={`adv-f-${i}`}>Field</label>
                <select id={`adv-f-${i}`} value={r.field}
                        onChange={(e) => changeField(i, e.target.value)}>
                  {fields.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                </select>

                <label className="sr-only" htmlFor={`adv-o-${i}`}>Test</label>
                <select id={`adv-o-${i}`} value={r.op}
                        onChange={(e) => set(i, { op: e.target.value as Operator })}>
                  {OPERATORS_FOR[type].map((op) => (
                    <option key={op} value={op}>{OPERATOR_LABEL[op]}</option>
                  ))}
                </select>

                <label className="sr-only" htmlFor={`adv-v-${i}`}>Value</label>
                {!needsValue ? (
                  <span className="muted">no value needed</span>
                ) : type === "boolean" ? (
                  <select id={`adv-v-${i}`} value={r.value || "true"}
                          onChange={(e) => set(i, { value: e.target.value })}>
                    <option value="true">yes</option>
                    <option value="false">no</option>
                  </select>
                ) : type === "enum" && r.op !== "in" ? (
                  <select id={`adv-v-${i}`} value={r.value}
                          onChange={(e) => set(i, { value: e.target.value })}>
                    <option value="">choose one</option>
                    {(spec?.options ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
                    {/* A value the lake has since grown is still selectable. */}
                    {r.value && !(spec?.options ?? []).includes(r.value) && (
                      <option value={r.value}>{r.value}</option>
                    )}
                  </select>
                ) : (
                  <input
                    id={`adv-v-${i}`}
                    type={type === "number" ? "number" : type === "date" ? "date" : "text"}
                    value={r.value}
                    onChange={(e) => set(i, { value: e.target.value })}
                  />
                )}

                <button type="button" onClick={() => remove(i)}
                        aria-label={`Remove this filter on ${spec?.label ?? r.field}`}>
                  Remove
                </button>

                {/* A format, in the slot this component already has for one.
                    It used to sit in the placeholder as "one, two, three",
                    where a format reads as a value the box already holds. */}
                {r.op === "in" && <span className="muted hint">Comma separated.</span>}
                {spec?.hint && <span className="muted hint">{spec.hint}</span>}
              </div>
            );
          })}

          <div className="bar">
            <button type="button" onClick={add}>Add a filter</button>
            {rules.length > 0 && (
              <button type="button" onClick={() => onChange([])}>Clear them all</button>
            )}
          </div>
        </div>
      )}

      {/* Nothing set and the panel shut: one button and no explanation, because
          the label already says what it does. */}
      {open && !embedded && rules.length === 0 && (
        <p className="muted">
          Filter on any field. Add as many as you need.
        </p>
      )}
    </div>
  );
}
